import React, { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw, AlertCircle } from "lucide-react";

/**
 * Lightweight, dependency-free math CAPTCHA.
 * - Renders "What is <a> + <b> ?" or subtraction
 * - Calls onValid(true|false) whenever the answer changes so parent can gate the submit button
 * - Shows an inline "Incorrect answer" error the moment the user's typed answer is wrong
 * - Refresh generates a fresh challenge
 */
export default function MathCaptcha({ onValid, testidPrefix = "captcha", label = "Verify you are human" }) {
  const [a, setA] = useState(0);
  const [b, setB] = useState(0);
  const [op, setOp] = useState("+");
  const [answer, setAnswer] = useState("");
  const [touched, setTouched] = useState(false);

  const regenerate = useCallback(() => {
    const x = Math.floor(Math.random() * 9) + 2;
    const y = Math.floor(Math.random() * 8) + 1;
    const useMinus = Math.random() > 0.5;
    if (useMinus && x > y) { setA(x); setB(y); setOp("-"); }
    else { setA(x); setB(y); setOp("+"); }
    setAnswer("");
    setTouched(false);
  }, []);

  useEffect(() => { regenerate(); }, [regenerate]);

  const expected = op === "+" ? a + b : a - b;
  const isCorrect = answer !== "" && Number(answer) === expected;
  const isWrong = touched && answer !== "" && !isCorrect;

  useEffect(() => {
    onValid?.(isCorrect);
  }, [isCorrect, onValid]);

  return (
    <div data-testid={`${testidPrefix}-wrap`}>
      <Label className="text-navy/70 text-xs">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        <div className="px-3 py-2 border border-navy/20 bg-slateBg serif-display text-navy text-lg select-none whitespace-nowrap" aria-hidden="true">
          {a} {op} {b} = ?
        </div>
        <Input
          type="text"
          inputMode="numeric"
          pattern="-?\d*"
          value={answer}
          onChange={(e) => { setAnswer(e.target.value.replace(/[^\d-]/g, "")); setTouched(true); }}
          onBlur={() => setTouched(true)}
          className={`max-w-[100px] ${isWrong ? "border-red-500 focus-visible:ring-red-400" : ""}`}
          placeholder="Answer"
          data-testid={`${testidPrefix}-input`}
          aria-label="Captcha answer"
          aria-invalid={isWrong ? "true" : "false"}
        />
        <button
          type="button"
          onClick={regenerate}
          className="p-2 text-navy/60 hover:text-navy border border-navy/10 hover:border-navy/40 transition-colors"
          aria-label="New captcha"
          data-testid={`${testidPrefix}-refresh`}
        >
          <RefreshCw size={14} />
        </button>
      </div>
      {isWrong && (
        <div className="mt-2 flex items-center gap-1.5 text-red-600 text-xs" role="alert" data-testid={`${testidPrefix}-error`}>
          <AlertCircle size={12} />
          <span>Incorrect answer. Please try again.</span>
        </div>
      )}
    </div>
  );
}
